import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Table,
  Card,
  Space,
  DatePicker,
  Select,
  Button,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Drawer,
} from 'antd'
import {
  DownloadOutlined,
  EyeOutlined,
  AuditOutlined,
  UserOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { auditsApi } from '@/api/audits'
import type { AuditLog } from '@/types/audit'

const { RangePicker } = DatePicker
const { Text } = Typography

export const AuditDashboard = () => {
  const [userIdFilter] = useState<string | undefined>()
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>()
  const [actionFilter, setActionFilter] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([
    dayjs().subtract(7, 'days'),
    dayjs(),
  ])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [selectedAudit, setSelectedAudit] = useState<AuditLog | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)

  // Query for audit logs
  const { data: auditsData, isLoading } = useQuery({
    queryKey: ['audits', { userIdFilter, categoryFilter, actionFilter, dateRange, page, pageSize }],
    queryFn: () =>
      auditsApi.list({
        userId: userIdFilter,
        category: categoryFilter,
        action: actionFilter,
        startDate: dateRange?.[0]?.toISOString(),
        endDate: dateRange?.[1]?.toISOString(),
        page,
        pageSize,
      }),
  })

  // Query for statistics
  const { data: statsData } = useQuery({
    queryKey: ['audit-stats', { dateRange }],
    queryFn: () =>
      auditsApi.getStats({
        startDate: dateRange?.[0]?.toISOString(),
        endDate: dateRange?.[1]?.toISOString(),
      }),
  })

  const handleExportCSV = () => {
    if (!auditsData?.data?.audits) return

    const audits = auditsData.data.audits
    const headers = ['Date', 'User ID', 'Category', 'Action', 'IP', 'Details']
    const rows = audits.map((audit) => [
      dayjs(audit.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      audit.user_id || 'N/A',
      audit.category || 'N/A',
      audit.action || 'N/A',
      audit.ip || 'N/A',
      JSON.stringify(audit.details || {}),
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-log-${dayjs().format('YYYY-MM-DD')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)

    message.success('Audit log exported successfully')
  }

  const handleViewDetails = (audit: AuditLog) => {
    setSelectedAudit(audit)
    setDrawerVisible(true)
  }

  const getCategoryTag = (category: string | null) => {
    const categoryConfig: Record<string, { color: string }> = {
      AUTH: { color: 'blue' },
      USER_MANAGEMENT: { color: 'green' },
      PERMISSION: { color: 'purple' },
      HOSPITAL: { color: 'orange' },
      SYSTEM: { color: 'default' },
    }
    const config = categoryConfig[category || 'SYSTEM'] || { color: 'default' }
    return <Tag color={config.color}>{category || 'Unknown'}</Tag>
  }

  const getActionTag = (action: string | null) => {
    const actionConfig: Record<string, { color: string }> = {
      CREATE: { color: 'green' },
      UPDATE: { color: 'blue' },
      DELETE: { color: 'red' },
      LOGIN: { color: 'cyan' },
      LOGOUT: { color: 'default' },
    }
    const config = actionConfig[action || ''] || { color: 'default' }
    return <Tag color={config.color}>{action || 'Unknown'}</Tag>
  }

  const columns: ColumnType<AuditLog>[] = [
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm:ss'),
      width: 180,
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      ellipsis: true,
      width: 280,
      render: (userId: string | null) => userId || <Text type="secondary">System</Text>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string | null) => getCategoryTag(category),
      width: 150,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action: string | null) => getActionTag(action),
      width: 120,
    },
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
      width: 140,
      render: (ip: string | null) => ip || <Text type="secondary">N/A</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
          Details
        </Button>
      ),
    },
  ]

  const stats = statsData?.data
  const audits = auditsData?.data

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Statistics Row */}
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Audit Logs"
                value={stats?.total || 0}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Unique Users"
                value={stats?.topUsers?.length || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Categories"
                value={stats?.byCategory?.length || 0}
                prefix={<AuditOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Actions" value={stats?.byAction?.length || 0} />
            </Card>
          </Col>
        </Row>

        {/* Main Table Card */}
        <Card
          title="Audit Log"
          extra={
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={!audits?.audits?.length}
            >
              Export CSV
            </Button>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Filters */}
            <Space wrap>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                format="YYYY-MM-DD"
              />
              <Select
                style={{ width: 180 }}
                placeholder="Filter by category"
                value={categoryFilter}
                onChange={setCategoryFilter}
                allowClear
                options={[
                  { label: 'Auth', value: 'AUTH' },
                  { label: 'User Management', value: 'USER_MANAGEMENT' },
                  { label: 'Permission', value: 'PERMISSION' },
                  { label: 'Hospital', value: 'HOSPITAL' },
                  { label: 'System', value: 'SYSTEM' },
                ]}
              />
              <Select
                style={{ width: 150 }}
                placeholder="Filter by action"
                value={actionFilter}
                onChange={setActionFilter}
                allowClear
                options={[
                  { label: 'Create', value: 'CREATE' },
                  { label: 'Update', value: 'UPDATE' },
                  { label: 'Delete', value: 'DELETE' },
                  { label: 'Login', value: 'LOGIN' },
                  { label: 'Logout', value: 'LOGOUT' },
                ]}
              />
            </Space>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={audits?.audits || []}
              rowKey="id"
              loading={isLoading}
              pagination={{
                current: page,
                pageSize,
                total: audits?.pagination?.total || 0,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} audit logs`,
                onChange: (newPage, newPageSize) => {
                  setPage(newPage)
                  setPageSize(newPageSize)
                },
              }}
              scroll={{ x: 1200 }}
            />
          </Space>
        </Card>
      </Space>

      {/* Details Drawer */}
      <Drawer
        title="Audit Log Details"
        placement="right"
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setSelectedAudit(null)
        }}
        width={600}
      >
        {selectedAudit && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Timestamp:</Text>
              <div>{dayjs(selectedAudit.createdAt).format('MMMM D, YYYY HH:mm:ss')}</div>
            </div>
            <div>
              <Text strong>User ID:</Text>
              <div>{selectedAudit.user_id || 'System'}</div>
            </div>
            <div>
              <Text strong>Category:</Text>
              <div>{getCategoryTag(selectedAudit.category)}</div>
            </div>
            <div>
              <Text strong>Action:</Text>
              <div>{getActionTag(selectedAudit.action)}</div>
            </div>
            <div>
              <Text strong>IP Address:</Text>
              <div>{selectedAudit.ip || 'N/A'}</div>
            </div>
            <div>
              <Text strong>User Agent:</Text>
              <div style={{ wordBreak: 'break-word' }}>{selectedAudit.agent || 'N/A'}</div>
            </div>
            {selectedAudit.details && (
              <div>
                <Text strong>Details:</Text>
                <pre
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '400px',
                  }}
                >
                  {JSON.stringify(selectedAudit.details, null, 2)}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  )
}
